import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PinInput from "../../components/PinInput";
import postDataToServer, {
  API_ROUTES,
  fetchAndStoreServerPublicKey,
  decryptResponseMessages,
  ensureLocation,
  getProfile,
  registerFcmTokenOnLogin,
} from "../../utils/apiClient";
import rsaController from "../../utils/RSA-controller";
import Popup from "../../components/Popup";
import AppConstants from "../../utils/appConstants";
import { getWebFcmToken } from "../../services/firebaseMessagingService";
import { consumeWhishReturn } from "../../utils/whishReturn";
import { useToast } from "../../contexts/ToastContext";

function maskEmail(email) {
  if (!email) return "";
  const [name, domain] = email.split("@");
  if (!domain) return email;
  if (name.length <= 2) {
    return `${name[0]}***@${domain}`;
  }
  const visible = name.slice(0, 2);
  const masked = "*".repeat(Math.max(0, name.length - 2));
  return `${visible}${masked}@${domain}`;
}

function autoSendStorageKey(email) {
  return `device_otp_autosend_${String(email || "").trim().toLowerCase()}`;
}

function toCodeNumber(value) {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize encrypted/plain API payloads.
 * Backend success for send-mail is CODE 200 (responseOk), not 604.
 */
function parseDeviceVerificationResponse(response) {
  const httpStatus = response?.status;
  let data =
    response?.decryptedData ||
    response?.data?.body ||
    response?.data?.data ||
    response?.data ||
    null;

  const stillEncrypted =
    data &&
    typeof data === "object" &&
    Array.isArray(data.MESSAGES) &&
    data.CODE == null &&
    data.code == null;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      const codeMatch = data.match(/"CODE"\s*:\s*(\d+)/);
      data = codeMatch ? { CODE: parseInt(codeMatch[1], 10) } : {};
    }
  }

  if (!data || typeof data !== "object") {
    return {
      data: {},
      responseCode: null,
      httpStatus,
      stillEncrypted: false,
    };
  }

  const responseCode = toCodeNumber(
    data.CODE ??
      data.code ??
      data.body?.CODE ??
      data.body?.code ??
      data.data?.CODE ??
      data.data?.code ??
      data.MESSAGES?.CODE ??
      data.MESSAGES?.code
  );

  return {
    data,
    responseCode,
    httpStatus,
    stillEncrypted: Boolean(stillEncrypted),
  };
}

const SEND_SUCCESS_CODES = new Set([
  AppConstants.responseOk, // 200 — email sent
  AppConstants.messagesOK, // 604
  AppConstants.responseAlreadyOk, // 205 — already sent recently
]);

const SEND_FAILURE_CODES = new Set([
  AppConstants.messagesErrorSendingSMS, // 603
  AppConstants.messagesErrorOTP, // 602
  AppConstants.catchError, // 707
  AppConstants.invalidInputs, // 708
  AppConstants.invalidSecretKey, // 706
  AppConstants.loginBlocked, // 502
  AppConstants.responseFailed, // 207
  AppConstants.responseNotFound, // 206
]);

export default function DeviceVerification() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { success: showSuccess, error: showErrorToast } = useToast();
  const email =
    (location.state && location.state.email) ||
    sessionStorage.getItem("pending_login_email") ||
    localStorage.getItem("userEmail") ||
    "";
  const pendingPassword =
    (location.state && location.state.password) ||
    sessionStorage.getItem("pending_login_password") ||
    "";
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState({});
  const [accountType] = useState(0);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [resendDuration, setResendDuration] = useState(60);
  const timerRef = useRef(null);
  const autoSendTriggeredRef = useRef(false);
  const sendVerificationCodeRef = useRef(null);

  const clearPendingLoginCredentials = () => {
    try {
      sessionStorage.removeItem("pending_login_email");
      sessionStorage.removeItem("pending_login_password");
    } catch (_) {
      /* ignore */
    }
  };

  const completeLoginAfterVerification = async () => {
    if (!email || !pendingPassword) {
      clearPendingLoginCredentials();
      navigate("/home", { replace: true });
      return false;
    }

    try {
      const response = await postDataToServer(API_ROUTES.LOGIN, {
        email,
        password: pendingPassword,
      });

      const decryptedResult =
        (await decryptResponseMessages(response.data)) || response.data;

      const result = decryptedResult?.body || decryptedResult;
      const loginCode = toCodeNumber(result?.CODE ?? decryptedResult?.CODE);

      if (loginCode === AppConstants.loginWaitAccess) {
        // Device still not accepted — stay in verification flow.
        return false;
      }

      if (
        loginCode === AppConstants.loginBlocked ||
        loginCode === AppConstants.loginIpBlocked
      ) {
        const retryAfter = Number(
          result?.RETRY_AFTER ?? decryptedResult?.RETRY_AFTER ?? 0
        );
        clearPendingLoginCredentials();
        navigate("/home", {
          replace: true,
          state: { loginBlocked: true, retryAfter },
        });
        return false;
      }

      const loginOk =
        loginCode === AppConstants.responseOk ||
        Boolean(result?.TOKEN || decryptedResult?.TOKEN);

      if (!loginOk) {
        clearPendingLoginCredentials();
        navigate("/home", { replace: true });
        return false;
      }

      localStorage.setItem("userEmail", email);
      localStorage.setItem("isLoggedIn", "true");

      const token =
        result?.TOKEN ||
        decryptedResult?.TOKEN ||
        result?.token ||
        decryptedResult?.token;
      if (token) {
        localStorage.setItem("token", token);
        sessionStorage.setItem("token", token);
      }

      try {
        const profileResponse = await getProfile({ withDoc: 1 });
        const profileData =
          profileResponse?.decryptedData || profileResponse?.data;
        if (
          profileData?.CODE === AppConstants.responseOk &&
          profileData?.DATA?.ID
        ) {
          const userId = profileData.DATA.ID;
          localStorage.setItem("userId", userId.toString());
          sessionStorage.setItem("userId", userId.toString());
        }
      } catch (_) {
        /* continue even if profile fetch fails */
      }

      try {
        const webFcmToken = await getWebFcmToken();
        if (webFcmToken) {
          await registerFcmTokenOnLogin(email, webFcmToken);
        }
      } catch (_) {
        /* ignore FCM errors */
      }

      clearPendingLoginCredentials();

      const pendingWhish = consumeWhishReturn();
      navigate("/pages/dashboard", { replace: true });
      if (pendingWhish?.result === "success") {
        showSuccess(
          t("transaction_succeeded", {
            ns: "mobile",
            defaultValue: "Transaction succeeded.",
          })
        );
      } else if (pendingWhish?.result === "failed") {
        showErrorToast(
          t("transaction_failed", {
            ns: "mobile",
            defaultValue: "Transaction failed.",
          })
        );
      }
      return true;
    } catch (loginError) {
      const bodyCode = Number(
        loginError?.response?.data?.CODE ?? loginError?.response?.data?.code
      );
      if (bodyCode === AppConstants.loginWaitAccess) {
        return false;
      }
      if (
        bodyCode === AppConstants.loginBlocked ||
        bodyCode === AppConstants.loginIpBlocked
      ) {
        const retryAfter = Number(
          loginError?.response?.data?.RETRY_AFTER ??
            loginError?.response?.data?.retry_after ??
            0
        );
        clearPendingLoginCredentials();
        navigate("/home", {
          replace: true,
          state: { loginBlocked: true, retryAfter },
        });
        return false;
      }
      clearPendingLoginCredentials();
      navigate("/home", { replace: true });
      return false;
    }
  };

  const startCountdown = (durationInSeconds) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const seconds = Math.max(0, Number(durationInSeconds) || 60);
    setCountdownSeconds(seconds);
    timerRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const ensureDeviceLocation = async () => {
    try {
      const result = await ensureLocation();
      if (result && result.ok) {
        return true;
      }

      // Do not block OTP send on location — headers still get a fallback position.
      // Location hard-fail was causing false "Please try again" UX after email send.
      console.warn("[DeviceVerification] location unavailable, continuing with fallback", result);
      return true;
    } catch (e) {
      console.warn("[DeviceVerification] location check failed, continuing", e);
      return true;
    }
  };

  const markSendSuccess = (result, { clearError = true } = {}) => {
    if (clearError) setError("");
    const resendAfter = Number(result?.RESEND_AFTER ?? resendDuration) || resendDuration;
    setResendDuration(resendAfter);
    startCountdown(resendAfter);
    if (email) {
      try {
        sessionStorage.setItem(autoSendStorageKey(email), "1");
      } catch (_) {
        /* ignore */
      }
    }
  };

  const sendVerificationCode = async ({ isAutoSend = false } = {}) => {
    if (!email) {
      setError("Email is required");
      return;
    }

    setSendingCode(true);
    setError("");

    try {
      await ensureDeviceLocation();

      const response = await postDataToServer(
        API_ROUTES.VERIFY_EMAIL_DEVICE_ACCOUNT,
        {
          EMAIL: email,
          ACCOUNT_TYPE: String(accountType),
        }
      );

      const { data: result, responseCode, httpStatus, stillEncrypted } =
        parseDeviceVerificationResponse(response);

      // Email can succeed even when local decrypt of MESSAGES fails.
      // Treat any non-failure HTTP 200 as success (same idea as forget-password).
      const isSendSuccess =
        SEND_SUCCESS_CODES.has(responseCode) ||
        (httpStatus === 200 && stillEncrypted) ||
        (httpStatus === 200 && responseCode == null) ||
        (httpStatus === 200 &&
          result?.RESEND_AFTER != null &&
          !SEND_FAILURE_CODES.has(responseCode));

      if (isSendSuccess) {
        if (responseCode === AppConstants.responseAlreadyOk && !isAutoSend) {
          markSendSuccess(result, { clearError: false });
          setError(
            t("try_again_later") ||
              "You have already requested a verification code. Please try again later."
          );
        } else {
          markSendSuccess(result);
        }
        return;
      }

      if (responseCode === AppConstants.messagesErrorSendingSMS) {
        try {
          sessionStorage.removeItem(autoSendStorageKey(email));
        } catch (_) {
          /* ignore */
        }
        setError(
          t("please_try_again_failed_to_send_email") ||
            "Failed to send verification email. Please try again."
        );
        return;
      }

      console.warn("[DeviceVerification][sendCode] unexpected response", {
        responseCode,
        httpStatus,
        stillEncrypted,
        result,
        raw: response?.data,
      });

      // Last resort: if HTTP succeeded, prefer letting the user enter the OTP
      // instead of blocking with a generic error (email may already be sent).
      if (httpStatus === 200) {
        markSendSuccess(result);
        return;
      }

      try {
        sessionStorage.removeItem(autoSendStorageKey(email));
      } catch (_) {
        /* ignore */
      }
      setError(
        result?.message ||
          result?.MESSAGE ||
          t("please_try_again") ||
          "Failed to send verification code. Please try again."
      );
    } catch (error) {
      const parsedError = parseDeviceVerificationResponse(error.response || {});
      console.error("[DeviceVerification][sendCode] request error", {
        status: error.response?.status,
        parsedCode: parsedError.responseCode,
        stillEncrypted: parsedError.stillEncrypted,
        message: error.message,
        errorData: error.response?.data,
      });

      if (
        error.response?.status === 200 ||
        parsedError.stillEncrypted ||
        SEND_SUCCESS_CODES.has(parsedError.responseCode)
      ) {
        markSendSuccess(parsedError.data);
        return;
      }

      try {
        sessionStorage.removeItem(autoSendStorageKey(email));
      } catch (_) {
        /* ignore */
      }
      setError(
        parsedError.data?.message ||
          parsedError.data?.MESSAGE ||
          error.response?.data?.message ||
          error.response?.data?.MESSAGE ||
          "Failed to send verification code. Please try again."
      );
    } finally {
      setSendingCode(false);
    }
  };

  sendVerificationCodeRef.current = sendVerificationCode;

  useEffect(() => {
    const initializeAndSendCode = async () => {
      if (!email) return;

      try {
        // Reuse keys when possible — regenerating every mount breaks decrypt
        // under React StrictMode (response encrypted for a discarded key).
        await rsaController.getUserPublicKey(email);
        await fetchAndStoreServerPublicKey();

        const storageKey = autoSendStorageKey(email);
        let sendFlag = null;
        try {
          sendFlag = sessionStorage.getItem(storageKey);
        } catch (_) {
          sendFlag = null;
        }

        if (sendFlag === "1" || sendFlag === "pending") {
          if (sendFlag === "1") startCountdown(resendDuration);
          return;
        }

        if (autoSendTriggeredRef.current) return;
        autoSendTriggeredRef.current = true;
        try {
          sessionStorage.setItem(storageKey, "pending");
        } catch (_) {
          /* ignore */
        }

        if (sendVerificationCodeRef.current) {
          await sendVerificationCodeRef.current({ isAutoSend: true });
        }
      } catch (error) {
        try {
          sessionStorage.removeItem(autoSendStorageKey(email));
        } catch (_) {
          /* ignore */
        }
        setError("Failed to initialize. Please refresh the page.");
      }
    };

    initializeAndSendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleVerify = async () => {
    if (!code || code.length !== 5) {
      setError("Please enter the 5-digit code");
      return;
    }

    if (!email) {
      setError("Email is required");
      return;
    }

    setError("");

    try {
      await ensureDeviceLocation();
      setLoading(true);

      const response = await postDataToServer(
        API_ROUTES.VERIFY_OTP_DEVICE_ACCOUNT,
        {
          EMAIL: email,
          VERIFICATION_CODE: code,
          ACCOUNT_TYPE: accountType,
        }
      );

      const { data: result, responseCode, httpStatus, stillEncrypted } =
        parseDeviceVerificationResponse(response);

      if (
        result?.success ||
        responseCode === AppConstants.responseOk ||
        responseCode === AppConstants.messagesOK ||
        result?.status === "success" ||
        (httpStatus === 200 && stillEncrypted)
      ) {
        setError("");
        try {
          sessionStorage.removeItem(autoSendStorageKey(email));
        } catch (_) {
          /* ignore */
        }

        // Match Flutter wait-access: verify OTP → login → enter app.
        const loggedIn = await completeLoginAfterVerification();
        if (loggedIn) {
          return;
        }

        setPopupConfig({
          title: "Verification Successful",
          message:
            "Your device has been verified. Please sign in to continue.",
          buttonText: "OK",
          icon: "fa-circle-check",
        });
        setShowPopup(true);
      } else {
        setError(
          result?.message ||
            result?.MESSAGE ||
            "Verification failed. Please check your code and try again."
        );
      }
    } catch (error) {
      const statusCode = error.response?.status;
      const errorData = error.response?.data;
      console.error("[DeviceVerification][verifyCode] request error", {
        statusCode,
        errorData,
        rawMessage: error.message,
      });

      if (statusCode === AppConstants.deviceOk || statusCode === AppConstants.deviceNotFound) {
        setError(
          errorData?.message ||
            errorData?.MESSAGE ||
            "Invalid verification code. Please try again."
        );
      } else {
        setError(
          errorData?.message ||
            errorData?.MESSAGE ||
            "Verification failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdownSeconds > 0 || sendingCode || loading) {
      return;
    }
    try {
      sessionStorage.removeItem(autoSendStorageKey(email));
    } catch (_) {
      /* ignore */
    }
    await sendVerificationCode({ isAutoSend: false });
  };

  const closePopup = () => {
    setShowPopup(false);
    if (popupConfig.icon === "fa-circle-check") {
      clearPendingLoginCredentials();
      navigate("/home", { replace: true });
    }
  };

  return (
    <div className="page-container">
      <div className="device-card">
        <h1 className="device-title">{t("deviceVerification.title")}</h1>
        <p className="device-subtitle">
          {t("deviceVerification.subtitle")}{" "}
          <span className="device-subtitle-email">{maskEmail(email)}</span>
        </p>

        {sendingCode && (
          <p
            style={{
              color: "#ffd100",
              fontSize: "12px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Sending verification code...
          </p>
        )}

        <PinInput length={5} onChange={setCode} />

        {error && (
          <p
            style={{
              color: "#ff4444",
              fontSize: "12px",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            {error}
          </p>
        )}

        <p className="device-resend">
          {t("deviceVerification.resendQuestion")}
          <button
            type="button"
            onClick={handleResend}
            disabled={loading || sendingCode || countdownSeconds > 0}
          >
            {countdownSeconds > 0
              ? `${t("deviceVerification.resendAfter") || "Resend code after"} ${countdownSeconds}s`
              : t("deviceVerification.resendButton")}
          </button>
        </p>

        <button
          className="device-verify-btn"
          onClick={handleVerify}
          disabled={loading || code.length !== 5 || sendingCode}
        >
          <span>
            {loading ? t("common.loading") : t("deviceVerification.verifyButton")}
          </span>
        </button>
      </div>

      <Popup
        isOpen={showPopup}
        onClose={closePopup}
        title={popupConfig.title}
        message={popupConfig.message}
        buttonText={popupConfig.buttonText}
        icon={popupConfig.icon}
      />
    </div>
  );
}
